// ServicePanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import ApiClient from '../auth/AuthService';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Refresh, PowerSettingsNew, Search, RestartAlt } from '@mui/icons-material';

// Должно совпадать с MIN_DAYS_WITHOUT_COMMUNICATION на бэкенде.
// Бэкенд проверяет это значение независимо от фронтенда.
const MIN_DAYS_WITHOUT_COMMUNICATION = 45;

// Preview и Confirm reset проходят по всей таблице clients, поэтому им нужен
// запас по времени. Таймаут задаётся только для этих двух запросов — общий
// таймаут axios в AuthService (30 с) остаётся прежним.
const RESET_REQUEST_TIMEOUT_MS = 120000;

const ServicePanel = ({ user }) => {
  const [apiClient] = useState(ApiClient());

  const [isSending, setIsSending] = useState(null);
  const [pendingLeads, setPendingLeads] = useState([]);
  const [sentToday, setSentToday] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [error, setError] = useState(null);

  // --- RESET SENT FLAG ---
  const [locationZones, setLocationZones] = useState([]);
  const [days, setDays] = useState(String(MIN_DAYS_WITHOUT_COMMUNICATION));
  const [excludedZones, setExcludedZones] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const isDaysValid = /^\d+$/.test(days)
    && Number(days) >= MIN_DAYS_WITHOUT_COMMUNICATION;

  // Любое изменение формы обесценивает предыдущий предпросмотр,
  // поэтому "Confirm reset" снова становится недоступным.
  const invalidatePreview = () => {
    setPreviewCount(null);
    setResetResult(null);
    setResetError(null);
  };

  const extractError = (err, fallback) => {
    const data = err.response && err.response.data;
    if (data) {
      if (data.days) {
        return Array.isArray(data.days) ? data.days[0] : String(data.days);
      }
      if (data.excluded_zones) {
        return `Excluded zones: ${JSON.stringify(data.excluded_zones)}`;
      }
      if (data.detail) {
        return data.detail;
      }
    }
    return err.message || fallback;
  };

  // Загружает все блоки сразу, без перезагрузки страницы
  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statusResponse, pendingResponse, sentResponse, zonesResponse] = await Promise.all([
        apiClient.get('/api/service-panel/sending-status/'),
        apiClient.get('/api/service-panel/pending-leads/'),
        apiClient.get('/api/service-panel/sent-today/'),
        apiClient.get('/api/service-panel/location-zones/'),
      ]);

      setIsSending(statusResponse.is_sending);
      setPendingLeads(Array.isArray(pendingResponse.zones) ? pendingResponse.zones : []);
      setSentToday(Array.isArray(sentResponse.zones) ? sentResponse.zones : []);
      setLocationZones(
        Array.isArray(zonesResponse.location_zones) ? zonesResponse.location_zones : []
      );
    } catch (err) {
      console.error('Error fetching service panel data:', err);
      setError(err.message || 'Failed to load service panel data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Переключение флага is_sending. Только POST.
  const handleToggleSending = async () => {
    setIsToggling(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/service-panel/toggle-sending/', {});
      setIsSending(response.is_sending);
    } catch (err) {
      console.error('Error toggling sending status:', err);
      setError(err.message || 'Failed to change sending status. Please try again.');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDaysChange = (e) => {
    setDays(e.target.value);
    invalidatePreview();
  };

  const handleZoneToggle = (zone) => {
    setExcludedZones((previous) => (
      previous.includes(zone)
        ? previous.filter((item) => item !== zone)
        : [...previous, zone]
    ));
    invalidatePreview();
  };

  // Шаг 1: только подсчёт, ничего не изменяется.
  const handlePreviewReset = async () => {
    setIsPreviewing(true);
    setResetError(null);
    setResetResult(null);
    try {
      const response = await apiClient.post(
        '/api/service-panel/reset-sent/preview/',
        { days: Number(days), excluded_zones: excludedZones },
        { timeout: RESET_REQUEST_TIMEOUT_MS }
      );
      setPreviewCount(response.count);
    } catch (err) {
      console.error('Error previewing reset:', err);
      setPreviewCount(null);
      setResetError(extractError(err, 'Failed to preview the reset. Please try again.'));
    } finally {
      setIsPreviewing(false);
    }
  };

  // Шаг 2: сам UPDATE. Выполняется только по явному нажатию администратора.
  const handleConfirmReset = async () => {
    setIsResetting(true);
    setResetError(null);
    try {
      const response = await apiClient.post(
        '/api/service-panel/reset-sent/execute/',
        { days: Number(days), excluded_zones: excludedZones },
        { timeout: RESET_REQUEST_TIMEOUT_MS }
      );
      setResetResult(response.updated);
      setPreviewCount(null);
      // Количество ожидающих лидов изменилось — обновляем остальные блоки.
      await fetchAll();
    } catch (err) {
      console.error('Error executing reset:', err);
      setResetError(extractError(err, 'Failed to reset the sent flag. Please try again.'));
    } finally {
      setIsResetting(false);
    }
  };

  const renderZonesTable = (zones, emptyMessage) => {
    if (zones.length === 0) {
      return (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ marginTop: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Zone</strong></TableCell>
              <TableCell align="right"><strong>Count</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {zones.map((zone, index) => (
              <TableRow key={`${zone.location_zone}-${index}`}>
                <TableCell>{zone.location_zone || '(no zone)'}</TableCell>
                <TableCell align="right">{zone.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Box mt={4}>
        <Container maxWidth="lg">
          <Paper elevation={3}>
            <Box sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                <Typography variant="h5" gutterBottom>Service Panel</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchAll}
                  disabled={isLoading}
                >
                  Refresh
                </Button>
              </Box>

              {error && (
                <Typography variant="body1" color="error" sx={{ marginBottom: 2 }}>
                  {error}
                </Typography>
              )}

              {isLoading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}

              {!isLoading && (
                <>
                  {/* a) Статус рассылки */}
                  <Typography variant="h6" gutterBottom>Sending status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        marginRight: 3,
                        fontWeight: 'bold',
                        color: isSending ? 'green' : 'red',
                      }}
                    >
                      {isSending ? 'Sending: ON' : 'Sending: OFF'}
                    </Typography>
                    <Button
                      variant="contained"
                      color={isSending ? 'error' : 'success'}
                      startIcon={<PowerSettingsNew />}
                      onClick={handleToggleSending}
                      disabled={isToggling || isSending === null}
                    >
                      {isSending ? 'Turn sending OFF' : 'Turn sending ON'}
                    </Button>
                    {isToggling && <CircularProgress size={24} sx={{ marginLeft: 2 }} />}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* b) Лиды, ожидающие отправки */}
                  <Typography variant="h6" gutterBottom>Pending leads by zone</Typography>
                  {renderZonesTable(pendingLeads, 'No zones with more than 10 pending leads.')}

                  <Divider sx={{ my: 3 }} />

                  {/* c) Отправленные сегодня первые сообщения */}
                  <Typography variant="h6" gutterBottom>Sent today by zone</Typography>
                  {renderZonesTable(sentToday, 'No first messages have been sent today.')}

                  <Divider sx={{ my: 3 }} />

                  {/* d) Возврат клиентов в очередь рассылки */}
                  <Typography variant="h6" gutterBottom>Reset sent flag</Typography>
                  <Typography variant="body2" sx={{ marginBottom: 2 }}>
                    Returns long-untouched clients back into the sending queue
                    (sets <code>fl_sent = false</code>). Clients that already have a lead
                    or a recent communication are never touched.
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
                    <TextField
                      type="number"
                      label="Days without communication"
                      size="small"
                      sx={{ width: 240, marginRight: 3 }}
                      value={days}
                      onChange={handleDaysChange}
                      inputProps={{ min: MIN_DAYS_WITHOUT_COMMUNICATION }}
                      error={!isDaysValid}
                      helperText={
                        isDaysValid
                          ? `Minimum ${MIN_DAYS_WITHOUT_COMMUNICATION} days`
                          : `Value must be a whole number of at least ${MIN_DAYS_WITHOUT_COMMUNICATION}`
                      }
                    />
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Excluded location zones
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    Selected zones will NOT be reset. Selecting nothing resets clients
                    in all zones.
                  </Typography>

                  {locationZones.length === 0 ? (
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                      No location zones found in the database.
                    </Typography>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{ maxHeight: 220, overflowY: 'auto', padding: 1, marginBottom: 2 }}
                    >
                      <FormGroup>
                        {locationZones.map((zone) => (
                          <FormControlLabel
                            key={zone}
                            control={
                              <Checkbox
                                size="small"
                                checked={excludedZones.includes(zone)}
                                onChange={() => handleZoneToggle(zone)}
                              />
                            }
                            label={zone}
                          />
                        ))}
                      </FormGroup>
                    </Paper>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Search />}
                      onClick={handlePreviewReset}
                      disabled={!isDaysValid || isPreviewing || isResetting}
                      sx={{ marginRight: 2 }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<RestartAlt />}
                      onClick={handleConfirmReset}
                      disabled={previewCount === null || isPreviewing || isResetting}
                    >
                      Confirm reset
                    </Button>
                    {(isPreviewing || isResetting) && (
                      <CircularProgress size={24} sx={{ marginLeft: 2 }} />
                    )}
                  </Box>

                  {previewCount !== null && (
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      This will reset {previewCount} clients. Click "Confirm reset" to apply.
                    </Typography>
                  )}

                  {resetResult !== null && (
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'green' }}>
                      {resetResult} clients were reset.
                    </Typography>
                  )}

                  {resetError && (
                    <Typography variant="body1" color="error">
                      {resetError}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default ServicePanel;
